import {
  MessageIcon,
  MessageIconFill,
} from '@euler/app/components/sidebar/icons/MessageIcon';
import { useChatUnreadCount } from '@euler/app/flows/chat/lib/session/hooks/useChatUnreadCount';
import { useFeedUnreadCount } from '@euler/app/flows/feed/lib/unread/hooks/useFeedUnreadCount';
import { CenterTabButton } from '@euler/app/flows/home/components/CenterTabButton';
import {
  ProfileTabIcon,
  ProfileTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/profile-icon';
import {
  RecordsTabIcon,
  RecordsTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/records-icon';
import {
  TasksTabIcon,
  TasksTabIconFill,
} from '@euler/app/flows/home/components/tab-icons/tasks-icon';
import { useCreateOrder } from '@euler/app/flows/home/functions/useCreateOrder';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useAlert } from '@euler/functions/ux/useAlert';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, memo, useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TabBarItem } from './TabBarItem';

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        shadow: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          shadowColor: 'rgba(0, 0, 0, 0.35)',
          shadowOffset: {
            width: 0,
            height: 0,
          },
          shadowOpacity: 0.45,
          shadowRadius: rpx(5),
          elevation: 5,
        },
      }),
    [rpx],
  );
};

export const useTabBarHeight = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();
  return rpx(55) + Math.max(rpx(10), bottom);
};

export const TabBar: FC<
  BottomTabBarProps & {
    onOrderSuccess?: (orderNo: string) => void;
  }
> = memo(({ navigation, state }) => {
  const isDesktop = useIsDesktopLayout();
  const styles = useStyles();
  const { rpx } = useResponsiveContext();
  const activeTabName = state.routes[state.index].name;
  const alert = useAlert();
  const role = useCurrentUserRole();
  const roleHelper = useMemo(() => new OrgUserRoleHelper(role), [role]);
  const storeConfiguration = useStoreConfiguration();
  const height = useTabBarHeight();
  const inboxUnreadCount = useFeedUnreadCount() + useChatUnreadCount();

  const { bottom } = useSafeAreaInsets();

  const onDashboardPress = useCallback(() => {
    navigation.navigate('Dashboard', {});
  }, [navigation]);

  const canAccessServiceRecords =
    !storeConfiguration.restrictedRolesForServiceRecords?.length ||
    roleHelper.matchesAny(storeConfiguration.restrictedRolesForServiceRecords);

  const onHistoryPress = useCallback(() => {
    if (canAccessServiceRecords) {
      navigation.navigate('History', {});
    } else {
      void alert({
        title: '对不起，您当前没有权限查看服务记录!',
      });
    }
  }, [alert, canAccessServiceRecords, navigation]);

  const onInboxPress = useCallback(() => {
    navigation.navigate('Inbox', {});
  }, [navigation]);

  const onMePress = useCallback(() => {
    navigation.navigate('Me', {});
  }, [navigation]);

  // Shared with the desktop-layout FAB, which stands in for this button where
  // the strip doesn't render.
  const onCenterButtonPress = useCreateOrder();

  // Bottom tab bar is a phone/tablet affordance. On desktop the sidebar
  // hosts every tab already — rendering this strip would just be clutter.
  if (isDesktop) return null;

  return (
    <View
      css={`
        height: ${height}px;
        flex-direction: row;
        align-items: stretch;
        justify-content: space-between;
        padding-bottom: ${Math.max(rpx(10), bottom)}px;
        background-color: rgba(0, 0, 0, 0.1);
        padding-top: ${rpx(10)}px;
        padding-left: ${rpx(15)}px;
        padding-right: ${rpx(15)}px;
      `}
      style={styles.shadow}
    >
      <LinearGradient
        colors={['#f9fbfc', '#c4c5c7']}
        style={StyleSheet.absoluteFill}
      />
      <TabBarItem
        name="Dashboard"
        activeTabName={activeTabName}
        text="在厂车辆"
        icon={(size, color, active) =>
          active ? (
            <TasksTabIconFill size={size} fill={color} />
          ) : (
            <TasksTabIcon size={size} fill={color} />
          )
        }
        onPress={onDashboardPress}
      />
      <TabBarItem
        name="History"
        activeTabName={activeTabName}
        text="历史看板"
        disabled={!canAccessServiceRecords}
        icon={(size, color, active) =>
          active ? (
            <RecordsTabIconFill size={size} fill={color} />
          ) : (
            <RecordsTabIcon size={size} fill={color} />
          )
        }
        onPress={onHistoryPress}
      />
      <CenterTabButton onPress={onCenterButtonPress} />
      <TabBarItem
        name="Inbox"
        activeTabName={activeTabName}
        text="消息中心"
        icon={(size, color, active) =>
          active ? (
            <MessageIconFill size={size} fill={color} />
          ) : (
            <MessageIcon size={size} fill={color} />
          )
        }
        badge={<UnreadBadge count={inboxUnreadCount} variant="overlay" />}
        onPress={onInboxPress}
      />
      <TabBarItem
        name="Me"
        activeTabName={activeTabName}
        text="我的"
        icon={(size, color, active) =>
          active ? (
            <ProfileTabIconFill size={size} fill={color} />
          ) : (
            <ProfileTabIcon size={size} fill={color} />
          )
        }
        onPress={onMePress}
      />
    </View>
  );
});
